'use client'

import { useStateLogger } from '@/features/dev-tools/components/StateLogger'
import { useFlyerBuilderState } from '@/features/flyer/store/FlyerBuilderStateProvider'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import { useSitecoreState } from '@/features/sitecore/store/useSitecoreState'
import { getItineraryImageEndpoint } from '@/features/websphere/configs/external-endpoints'
import { useEnvironmentState } from '@/utils/environment/store/useEnvironmentState'
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect'
import { regionColors } from '@/utils/lib/utils'
import { useEffect, useMemo, useRef, useState } from 'react'

export const PreviewSyncronizer: React.FC = () => {
	const environmentState = useEnvironmentState()
	const flyerBuilderState = useFlyerBuilderState((state) => state)
	useStateLogger('flyer-builder', flyerBuilderState)
	const sitecoreStore = useSitecoreState((state) => state)
	const [prevCount, setPrevCount] = useState(flyerBuilderState.steps.itinerary.itineraries.length)

	const defaultMscLogoFlyer = 'https://int.mscbook.com/images/sdl/msc-cruises-logo.svg'
	const textNextToShipIconLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-TEXT-NEXT-TO-SHIP-ICON', 'Book now with your travel advisor | Visit msccruises.com')
	const flyerBuilderLogo = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-MSC-LOGO', defaultMscLogoFlyer)
	const defaultPromoImageApplyOfferStep = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-DEFAULT-PROMO-LOGO', '')
	const fromLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-FROM', 'From')
	const toLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-TO', 'To')

	const updatePreview = (query: string, value: string | number | null | undefined) => {
		const domElements = document.querySelectorAll(query)
		if (domElements && domElements.length > 0) {
			domElements.forEach((element) => {
				element.innerHTML = value?.toString() || ''
			})
		}
	}

	const updatePreviewImage = async (query: string, value: string | null | undefined, basePath: 'none' | 'websphere_images') => {
		const prefix = environmentState.getString('WEBSPHERE_IMAGES_ENDPOINT', '')
		const websphereResolvedEndpoint = value?.startsWith(prefix) ? value : `${prefix}/${value}`

		const domElements: NodeListOf<HTMLImageElement> = document.querySelectorAll(query)
		if (!domElements) return

		domElements.forEach((element) => {
			if (!value) {
				element.src = 'https://archive.org/download/placeholder-image/placeholder-image.jpg'
			} else {
				element.src = basePath === 'none' ? value : websphereResolvedEndpoint
			}
			if (element.getAttribute('src') !== '') element.style.border = 'none'
			else element.style.border = '1px solid #add8e6'
		})
	}

	// Destination step
	const destinationStep = flyerBuilderState.steps.destination
	useEffect(() => updatePreview('#destination-text', destinationStep.destination?.label.toUpperCase()), [destinationStep.destination])
	useEffect(() => {
		const textDestination = document.getElementById('destination-text')
		if (textDestination) {
			const destinationAreaCode: string | undefined = destinationStep.destination?.area
			const destinationTextColor = regionColors[destinationAreaCode ?? ''] ?? '#000000'
			textDestination.style.color = destinationTextColor
		}
	}, [destinationStep.destination?.area])
	useEffect(() => updatePreview('#flyer-title', destinationStep.headline || destinationStep.destination?.headline || ''), [destinationStep.headline, destinationStep.destination])
	useEffect(() => updatePreview('#flyer-description', destinationStep.copy || destinationStep.destination?.copy || ''), [destinationStep.copy, destinationStep.destination])
	useEffect(() => {
		// drag & drop functionality for hero image
		const flyerVisual = document.getElementById('flyer-visual')
		if (!flyerVisual) return

		const handleDrop = async (e: DragEvent) => {
			e.preventDefault()
			e.stopPropagation()
			const imageSrc = e.dataTransfer?.getData('text/plain')
			if (imageSrc) {
				flyerBuilderState.updateStepData('destination', { imageSrc })
			}
		}
		const handleDragOver = (e: DragEvent) => {
			e.preventDefault()
		}
		flyerVisual.addEventListener('drop', handleDrop)
		flyerVisual.addEventListener('dragover', handleDragOver)

		return () => {
			flyerVisual.removeEventListener('drop', handleDrop)
			flyerVisual.removeEventListener('dragover', handleDragOver)
		}
	}, [flyerBuilderState.steps.destination])

	// Itinerary step
	const currentItinerary = flyerBuilderState.steps.itinerary.itineraries[0]
	useEffect(() => updatePreview('#ship-text', currentItinerary?.ship?.label), [currentItinerary.ship])
	useEffect(() => updatePreview('#port-text', currentItinerary?.embarkationPort?.label), [currentItinerary.embarkationPort?.label])
	const currentItineraryDateRangeFormatted = useMemo(() => {
		if (!currentItinerary?.dateRange) return undefined
		const formattedDate = currentItinerary.dateRange.split('-')
		return `${fromLabel} ${formattedDate[0]} ${toLabel} ${formattedDate[1]}`
	}, [currentItinerary?.dateRange])
	useEffect(() => {
		updatePreview('#date-range', currentItineraryDateRangeFormatted)
	}, [currentItinerary?.dateRange])

	useEffect(() => updatePreview('#departure-day-text', currentItinerary.departureWeekDay), [currentItinerary.departureWeekDay])
	useEffect(() => updatePreview('#days-nights', currentItinerary.daysNights), [currentItinerary.daysNights])
	useEffect(() => {
		const { price, hsc } = currentItinerary.price ?? { price: 0, hsc: 0 }
		updatePreview('#price-without-hsc', price?.toString() || '0')
		updatePreview('#hsc-price', hsc?.toString() || '0')
		updatePreview('#full-price', (price + (hsc ?? 0)).toString() || '0')
	}, [currentItinerary.price])
	useAsyncEffect(() => updatePreviewImage('#flyer-itinerary-single .itinerary-image', currentItinerary.itineraryCode && getItineraryImageEndpoint(currentItinerary.itineraryCode), 'none'), [currentItinerary.itineraryCode])

	// Price list step
	const priceListStep = flyerBuilderState.steps.priceList.priceLists[0]
	useEffect(() => updatePreview('#below-price-paragraph', priceListStep.offer?.termsAndConditions), [priceListStep.offer?.termsAndConditions])
	useEffect(() => updatePreview('#promotion-text', priceListStep.priceList?.name.toUpperCase()), [priceListStep.priceList])
	useEffect(() => updatePreview('#flight-label', priceListStep.flightInformation), [priceListStep.flightInformation])
	useEffect(() => {
		const flightInformationFlightFromElement = document.getElementById('flight-from')
		if (flightInformationFlightFromElement) {
			//hide 'Flight from' if no text typed
			flightInformationFlightFromElement.style.display = ['<br>', undefined, ''].includes(priceListStep?.flightInformation) ? 'none' : 'block'
		}
	}, [priceListStep?.flightInformation])
	useAsyncEffect(() => updatePreviewImage('#flyer-visual-img', priceListStep.replaceWithHeroImage ? priceListStep.offer?.heroImage : destinationStep.imageSrc, 'websphere_images'), [destinationStep.imageSrc, priceListStep.replaceWithHeroImage])
	useAsyncEffect(() => updatePreviewImage('#promotion-image', priceListStep.promotionImage || defaultPromoImageApplyOfferStep, 'websphere_images'), [priceListStep.promotionImage])
	useEffect(() => {
		// Delete previous icons
		const elements = document.querySelectorAll('.promotion-icon')
		elements.forEach((element) => element.remove())

		if (!priceListStep.priceList?.icons) return

		const iconContainer = document.getElementById('promotion-icons')
		const promotionText = document.getElementById('promotion-text')
		priceListStep.priceList.icons.forEach((icon) => {
			const img = document.createElement('img')
			img.className = 'promotion-icon'
			img.style.height = '20px'
			img.style.width = '20px'
			img.crossOrigin = 'anonymous'
			img.src = environmentState.getString('WEBSPHERE_BASE_ENDPOINT', 'mscbook.com') + icon.url
			iconContainer?.insertBefore(img, promotionText)
		})
	}, [priceListStep.priceList])

	// Notes step
	const additionalNoteStep = flyerBuilderState.steps.notes
	useEffect(() => updatePreview('#user-input-notes-step', additionalNoteStep.additionalNote), [additionalNoteStep.additionalNote])

	// Agent details step
	const agentDetailsStep = flyerBuilderState.steps.agentDetails
	useEffect(() => updatePreview('.agencyName', agentDetailsStep.name), [agentDetailsStep.name])
	useEffect(() => updatePreview('.email', agentDetailsStep.email), [agentDetailsStep.email])
	useEffect(() => updatePreview('.phone', agentDetailsStep.phoneNumber), [agentDetailsStep.phoneNumber])
	useAsyncEffect(() => updatePreviewImage('.agency-logo', agentDetailsStep.logo, 'none'), [agentDetailsStep.logo])
	useEffect(() => {
		const instagramLogo = document.getElementById('instagramLogo')
		const facebookLogo = document.getElementById('facebookLogo')
		if (instagramLogo && facebookLogo) {
			instagramLogo.style.display = agentDetailsStep.socialInfo?.includes('instagram') ? 'block' : 'none'
			facebookLogo.style.display = agentDetailsStep.socialInfo?.includes('facebook') ? 'block' : 'none'
		}
		// update href, to include it as a usable link in the pdf generation
		const facebookOrInstagram = document.getElementById('facebookOrInstagram')
		if (facebookOrInstagram instanceof HTMLAnchorElement) {
			facebookOrInstagram.href = agentDetailsStep.socialInfo || ''
		}

		updatePreview('#facebookOrInstagram', agentDetailsStep.socialInfo)
	}, [agentDetailsStep.socialInfo])

	// default updates
	useEffect(() => updatePreview('#textNextToShipIcon', textNextToShipIconLabel), [textNextToShipIconLabel])
	useAsyncEffect(() => {
		// default promo image if none selected
		if (flyerBuilderState.getActiveStep() === 'priceList') {
			priceListStep.promotionImage || updatePreviewImage('#promotion-image', defaultPromoImageApplyOfferStep, 'websphere_images')
		}
	}, [flyerBuilderState.getActiveStep()])

	// -------------------------------------------------------------------------
	// Multiple itin case, preview updates
	const multipleItinTemplateRef = useRef<HTMLElement | null>(null)

	useEffect(() => {
		const multipleItinTemplate = document.getElementById('multiple-itin-1')
		if (multipleItinTemplate) {
			multipleItinTemplateRef.current = multipleItinTemplate.cloneNode(true) as HTMLElement
		}
	}, [])

	useEffect(() => {
		const itineraryCount = flyerBuilderState.steps.itinerary.itineraries.length
		const secondFlyerPage = document.getElementById('second-flyer-page')
		const secondPageSkeleton = document.getElementById('second-page-skeleton')
		const itinsParentDiv = document.getElementById('multiple-itin-parent')
		if (!secondFlyerPage || !secondPageSkeleton || !itinsParentDiv) return

		if (itineraryCount > 1) {
			const mainEl = secondFlyerPage.querySelector('main')
			if (mainEl && !mainEl.contains(secondPageSkeleton)) {
				mainEl.appendChild(secondPageSkeleton)
			}
			secondPageSkeleton.style.display = 'block'
		}

		if (!multipleItinTemplateRef.current) return
		const template = multipleItinTemplateRef.current
		const innerElements = [
			'itinerary-image',
			'destination-text',
			'ship-text',
			'port-text',
			'departure-day-text',
			'date-range',
			'promotion-text',
			'promotion-image',
			'price-without-hsc',
			'hsc-price',
			'full-price',
		]
		if (itineraryCount === 2) {
			if (!itinsParentDiv.querySelector(`#multiple-itin-1`)) {
				const firstNewItin = template.cloneNode(true) as HTMLElement
				const itinNumber = 1
				firstNewItin.id = `multiple-itin-${itinNumber}`
				firstNewItin.className = `multiple-itin-${itinNumber}`

				// Update inner elements with unique class names
				innerElements.forEach((el) => {
					const child = firstNewItin.querySelector(`.${el}-1`)
					if (child) {
						child.classList.remove(`${el}-1`) // Remove old class
						child.classList.add(`${el}-${itinNumber}`) // Add new class
					}
				})
				itinsParentDiv.appendChild(firstNewItin)
			}
		} else if (itineraryCount > 2) {
			const itinNumber = itineraryCount - 1
			const expectedItinId = `multiple-itin-${itinNumber}`

			if (!itinsParentDiv.querySelector(`#${expectedItinId}`)) {
				const newItin = template.cloneNode(true) as HTMLElement
				newItin.id = expectedItinId
				newItin.className = expectedItinId

				innerElements.forEach((el) => {
					const child = newItin.querySelector(`.${el}-1`)
					if (child) {
						child.classList.remove(`${el}-1`)
						child.classList.add(`${el}-${itinNumber}`)
					}
				})
				itinsParentDiv.appendChild(newItin)
			}
		}
		Array.from(itinsParentDiv.children).forEach((child) => {
			const match = child.id.match(/^multiple-itin-(\d+)$/)
			if (match && parseInt(match[1], 10) >= itineraryCount) {
				child.remove()
			}
		})

		updatePreview('.textNextToShipIcon', textNextToShipIconLabel)
	}, [flyerBuilderState.steps.itinerary.itineraries.length])

	// remove page page two, when there are no itins left in it.
	useEffect(() => {
		const itineraryCount = flyerBuilderState.steps.itinerary.itineraries.length
		if (prevCount > 1 && itineraryCount === 1) {
			const secondPageSkeleton = document.querySelectorAll('#second-page-skeleton')
			secondPageSkeleton.forEach((el) => {
				const element = el as HTMLElement
				element.style.display = 'none'
			})
		}
		setPrevCount(itineraryCount)
	}, [flyerBuilderState.steps.itinerary.itineraries.length, prevCount])

	// update the data for each itinerary in the multiple itin case
	const itinIndex = flyerBuilderState.steps.itinerary.itineraries.length - 1
	const currentItineraryInSecondPage = flyerBuilderState.steps.itinerary.itineraries[itinIndex] || null

	const currentItineraryDateRangeFormattedSecondPage = useMemo(() => {
		if (!currentItineraryInSecondPage?.dateRange) return undefined
		const formattedDate = currentItineraryInSecondPage.dateRange.split('-')
		return `${fromLabel} ${formattedDate[0]} ${toLabel} ${formattedDate[1]}`
	}, [currentItineraryInSecondPage?.dateRange])
	useEffect(() => {
		if (currentItineraryInSecondPage) {
			updatePreview(`.destination-text-${itinIndex}`, destinationStep.destination?.label.toUpperCase()), [destinationStep.destination]
			updatePreview(`.ship-text-${itinIndex}`, currentItineraryInSecondPage.ship?.label)
			updatePreview(`.port-text-${itinIndex}`, currentItineraryInSecondPage.embarkationPort?.label)
			updatePreview(`.date-range-${itinIndex}`, currentItineraryDateRangeFormattedSecondPage)
			updatePreview(`.departure-day-text-${itinIndex}`, currentItineraryInSecondPage.departureWeekDay)
			updatePreview(`.days-nights-${itinIndex}`, currentItineraryInSecondPage.daysNights)

			const { price, hsc } = currentItineraryInSecondPage.price ?? { price: 0, hsc: 0 }
			updatePreview(`.price-without-hsc-${itinIndex}`, price?.toString() || '0')
			updatePreview(`.hsc-price-${itinIndex}`, hsc?.toString() || '0')
			updatePreview(`.full-price-${itinIndex}`, (price + (hsc ?? 0)).toString() || '0')
		}
	}, [
		currentItineraryInSecondPage,
		currentItineraryDateRangeFormattedSecondPage,
		itinIndex,
	])

	useAsyncEffect(() => {
		if (currentItineraryInSecondPage?.itineraryCode) {
			updatePreviewImage(`.itinerary-image-${itinIndex}`, getItineraryImageEndpoint(currentItineraryInSecondPage.itineraryCode), 'none')
		}
	}, [currentItineraryInSecondPage?.itineraryCode])

	// update promotion image for each itinerary in the multiple itin case
	const priceListsByItinerary = flyerBuilderState.steps.priceList.priceLists
	useEffect(() => {
		// Ensure the UI updates dynamically for all itineraries
		flyerBuilderState.steps.itinerary.itineraries.forEach((_, index) => {
			const priceList = priceListsByItinerary[index]?.priceList
			if (priceList) {
				updatePreview(`.promotion-text-${index}`, priceList.name.toUpperCase())
				updatePreview(`.price-without-hsc-${index}`, priceList.minPrice.price.toString())
				const hscValue = priceList.minPrice.hsc !== undefined && priceList.minPrice.hsc !== null ? priceList.minPrice.hsc.toString() : '0'
				updatePreview(`.hsc-price-${index}`, hscValue)
				const fullPrice = priceList.minPrice.price + (priceList.minPrice.hsc ?? 0)
				updatePreview(`.full-price-${index}`, fullPrice.toString())
			}
		})
	}, [priceListsByItinerary, flyerBuilderState.steps.itinerary.itineraries.length])
	useAsyncEffect(() => {
		flyerBuilderState.steps.itinerary.itineraries.forEach((_, index) => {
			const promotionImage = priceListsByItinerary[index]?.promotionImage
			if (promotionImage) {
				updatePreviewImage(`.promotion-image-${index}`, promotionImage || defaultPromoImageApplyOfferStep, 'websphere_images')
			}
		})
	}, [priceListsByItinerary, flyerBuilderState.steps.itinerary.itineraries.length])
	return <></>
}
